"use client";
import { Quote } from "@areahub/icons";
import { ButtonProps, Flex, Heading } from "@chakra-ui/react";
import { SectionTemplate } from "../section-template";
import { ImageFragment } from "lib/gcms";
import { RichTextContent } from "@graphcms/rich-text-types";
import {
  Carousel,
  CarouselIconButton,
  CarouselSlide,
  useCarousel,
} from "@ui/components";
import { Box, Text } from "@chakra-ui/react";
import { RichText } from "@graphcms/rich-text-react-renderer";
import { customRichText } from "components/individuals/mdx-components";
import { useEffect, useState } from "react";
import { IoChevronBackOutline, IoChevronForwardOutline } from "react-icons/io5";
import CarouselDotIndicators from "@ui/components/carousel/carousel-dot-indicators";

export interface TestimonialProps {
  /**
   * The person's first name
   */
  name?: string;
  /**
   * Text of the testimonial
   */
  content?: {
    raw: RichTextContent;
  };
  /**
   * The person's job title
   */
  jobTitle?: string;
  /**
   * The title of the testimonial
   */
  highlight?: string;
  /**
   * The location of the person
   */
  location?: string;
  /**
   * The image associated with the person
   */
  picture?: ImageFragment & { alt?: string };
}

export interface TestimonialSectionProps {
  /**
   * Background color of the section
   */
  bg: string;
  /**
   * Text color of the section
   */
  color: string;
  /**
   * Title of the section
   */
  title?: string | null;
  /**
   * An array of testimonial data
   */
  testimonials: TestimonialProps[];
}

export function Testimonials({
  bg,
  color,
  testimonials,
}: TestimonialSectionProps) {
  return (
    <SectionTemplate {...{ bg, color }} borderRadius={0}>
      <Flex direction="column" align="center" textAlign="center">
        <Quote color={color} fontSize={{ base: "4xl", md: "7xl" }} />
      </Flex>

      <B2BTestimonialsSection testimonials={testimonials} color={color} />
    </SectionTemplate>
  );
}

const B2BTestimonialsSection = ({
  testimonials,
  color,
}: {
  testimonials: TestimonialSectionProps["testimonials"];
  color: string;
}) => {
  const isSingular = testimonials.length === 1 ? true : false;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoaded, setLoaded] = useState(false);
  const [sliderRef, slider] = useCarousel({
    loop: isSingular ? false : true,
    slides: {
      perView: 1,
    },
    created: (slider) => {
      setLoaded(true);
      let timeout: ReturnType<typeof setTimeout>;
      let mouseOver = false;
      function clearNextTimeout() {
        clearTimeout(timeout);
      }
      function nextTimeout() {
        clearTimeout(timeout);
        if (mouseOver) return;

        timeout = setTimeout(() => {
          slider.next();
        }, 5000);
      }
      nextTimeout();

      slider.container.addEventListener("mouseover", () => {
        mouseOver = true;
        clearNextTimeout();
        slider.container.addEventListener("mouseout", () => {
          mouseOver = false;
          nextTimeout();
        });
      });
      slider.on("dragStarted", clearNextTimeout);
      slider.on("animationEnded", nextTimeout);
      slider.on("updated", nextTimeout);
    },
  });

  function prevSlide() {
    slider.current?.prev();
  }
  function nextSlide() {
    slider.current?.next();
  }
  function setSlide(idx: number) {
    slider.current?.moveToIdx(idx);
  }

  useEffect(() => {
    slider.current?.on("slideChanged", (slider) => {
      setCurrentSlide(slider.track.details.rel);
    });
  }, [currentSlide]);

  const sharedArrowStyles: ButtonProps = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
  };

  return (
    <>
      <Flex
        flexDir={{
          base: "column",
          lg: "row",
        }}
        pt={{
          lg: 10,
        }}
        align={{
          base: "center",
          lg: "start",
        }}
        justify={{
          base: "center",
          lg: "space-between",
        }}
        gap={10}
      >
        <Carousel ref={sliderRef} mx="auto" maxW="4xl" w="100%">
          {testimonials.map((testimonial, idx) => {
            return (
              <CarouselSlide key={idx} textAlign="center">
                <Box
                  w={{
                    base: "90%",
                    md: "unset",
                  }}
                  mx="auto"
                  h="full"
                >
                  <Flex
                    flexDir={"column"}
                    key={`testimonial-${idx}`}
                    alignItems={"center"}
                    justifyContent={"center"}
                    h="full"
                  >
                    <Box
                      h={{
                        lg: "135px",
                      }}
                      textAlign={{
                        base: "center",
                        lg: "left",
                      }}
                      mb={{ base: 5, lg: 10 }}
                    >
                      <RichText
                        content={testimonial?.content?.raw as RichTextContent}
                        renderers={customRichText(color, undefined, undefined, {
                          base: 14,
                          md: 18,
                          xl: 20,
                        })}
                      />
                    </Box>
                    <Text
                      fontStyle={"italic"}
                      fontSize={{
                        base: 14,
                        md: 18,
                        xl: 20,
                      }}
                      w={"60%"}
                    >
                      {testimonial.name}
                    </Text>
                    <Text
                      fontWeight={"bold"}
                      fontSize={{
                        base: 14,
                        md: 18,
                        xl: 20,
                      }}
                    >
                      {testimonial.jobTitle}
                    </Text>
                    <Text
                      fontWeight={"bold"}
                      fontSize={{
                        base: 14,
                        md: 18,
                        xl: 20,
                      }}
                    >
                      {testimonial.location}
                    </Text>
                  </Flex>
                </Box>
              </CarouselSlide>
            );
          })}
        </Carousel>

        {!isSingular && (
          <>
            {isLoaded && (
              <>
                <CarouselIconButton
                  {...sharedArrowStyles}
                  left={{
                    base: "5px",
                    xl: "10%",
                  }}
                  color={"white"}
                  onClick={prevSlide}
                  icon={<IoChevronBackOutline />}
                  aria-label="Previous Slide"
                />
                <CarouselIconButton
                  {...sharedArrowStyles}
                  right={{
                    base: "5px",
                    xl: "10%",
                  }}
                  color={"white"}
                  onClick={nextSlide}
                  icon={<IoChevronForwardOutline />}
                  aria-label="Next Slide"
                />
              </>
            )}
          </>
        )}
      </Flex>
      {!isSingular && (
        <CarouselDotIndicators
          length={testimonials.length}
          activeIndex={currentSlide}
          setActiveIndex={setSlide}
        />
      )}
    </>
  );
};
